import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, WritableSignal, computed, signal } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { forkJoin, Observable } from 'rxjs';
import { filter, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { ArrayHelper, MaskPipe } from 'tiime-expert-utils';
import {
  FallbackTextDirective,
  OverlayBase,
  TIIME_OVERLAY_DATA,
  TiimeButtonModule,
  TiimeIconModule,
  TiimeIconPropertyDirective,
  TiimeMenuModule,
  TiimeProgressSpinnerModule,
  TiimePropertiesDirective,
  TiimeSnackbarService,
  TiimeTooltipModule
} from 'tiime-material';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { CompanyApiService } from '@api-services/company-api.service';
import { ExternalLinkApiService } from '@api-services/external-link-api.service';
import { PermissionApiService } from '@api-services/permission-api.service';
import { UserApiService } from '@api-services/user-api.service';
import { SIRET_MASK } from '@constants/mask.constant';
import { ContributorJobType } from '@enums/contributor-job-type.enum';
import { PermissionAcl } from '@enums/permission-acl.enum';
import { Contract } from '@gi/models/contract';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { CompanyContributor } from '@models/company-contributor';
import { ContributorJob } from '@models/contributor-job';
import { ExternalLink } from '@models/external-link';
import { LyaStatus } from '@models/lya-status';
import { Permission } from '@models/permission';
import { TaxRegime } from '@models/tax-regime';
import { User } from '@models/user';
import { VatSystem } from '@models/vat-system';
import { ChangeContractStatusType, ContractFacade } from '@modules/core/facades/contract.facade';
import { CompanyRoute } from '@modules/core/navigation/company-route.enum';
import { NavigationService } from '@modules/core/navigation/navigation.service';
import { businessUserSelector } from '@modules/core/store/business-user/business-user-selector';
import { ExternalUrlService } from '@services/external-url.service';
import { AddressPropertyComponent } from '@shared-components/address-property/address-property.component';
import { AppsAccessButtonComponent } from '@shared-components/apps-access-button/apps-access-button.component';
import { CompanyContributorsContactsComponent } from '@shared-components/company-contributors-contacts/company-contributors-contacts.component';
import { CompanyStatusComponent } from '@shared-components/company-status/company-status.component';
import { CompanyUsersContactsComponent } from '@shared-components/company-users-contacts/company-users-contacts.component';
import { ContractsContainerComponent } from '@shared-components/contracts-container/contracts-container.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { CreateOrUpdateExternalLinkDialogData } from '@shared-dialogs/create-or-update-external-link-dialog/create-or-update-external-link-dialog-data';
import { CreateOrUpdateExternalLinkDialogService } from '@shared-dialogs/create-or-update-external-link-dialog/create-or-update-external-link-dialog.service';
import { ExonerationZonesDialogModule } from '@shared-dialogs/exoneration-zones-dialog/exoneration-zones-dialog.module';
import { ExonerationZonesDialogService } from '@shared-dialogs/exoneration-zones-dialog/exoneration-zones-dialog.service';
import { SendToLyaDialogData } from '@shared-dialogs/send-to-lya-dialog/send-to-lya-dialog-data';
import { SendToLyaDialogService } from '@shared-dialogs/send-to-lya-dialog/send-to-lya-dialog.service';
import { ReturnedUpdateContractStatusDialogData } from '@shared-dialogs/update-contract-status-dialog/returned-update-contract-status-dialog-data';
import { HasRoleDirective } from '@shared-directives/has-role/has-role.directive';
import { CodeAndLabelPipe } from '@shared-pipes/code-and-label/code-and-label.pipe';
import { CompanyInfosLabelPipe } from '@shared-pipes/company-infos-label/company-infos-label.pipe';
import { TaxRegimeLabelPipe } from '@shared-pipes/tax-regime-label/tax-regime-label.pipe';

import { CompanyOverviewOverlayData } from './company-overview-overlay-data';

@Component({
  selector: 'app-company-overview-overlay',
  standalone: true,
  imports: [
    CommonModule,
    TiimeButtonModule,
    TiimeTooltipModule,
    TiimeIconModule,
    TiimeMenuModule,
    TiimePropertiesDirective,
    TiimeIconPropertyDirective,
    TiimeProgressSpinnerModule,
    AppsAccessButtonComponent,
    CompanyUsersContactsComponent,
    CompanyContributorsContactsComponent,
    CompanyStatusComponent,
    ExonerationZonesDialogModule,
    HasRoleDirective,
    CompanyInfosLabelPipe,
    AddressPropertyComponent,
    FallbackTextDirective,
    TaxRegimeLabelPipe,
    CodeAndLabelPipe,
    MaskPipe,
    ContractsContainerComponent
  ],
  templateUrl: './company-overview-overlay.component.html',
  styleUrls: ['./company-overview-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyOverviewOverlayComponent extends OverlayBase<void> implements OnInit {
  readonly vatSystems$: Observable<VatSystem[]> = this.commonDataApiService.getVatSystems();
  readonly taxRegimes$: Observable<TaxRegime[]> = this.commonDataApiService.getTaxRegimes();

  readonly contracts: WritableSignal<Contract[]> = signal<Contract[]>(null);
  readonly users: WritableSignal<User[]> = signal<User[]>(null);
  readonly companyContributors: WritableSignal<CompanyContributor[]> = signal<CompanyContributor[]>(null);
  readonly contributorJobs: WritableSignal<ContributorJob[]> = signal<ContributorJob[]>(null);
  readonly company: WritableSignal<Company> = signal<Company>(null);
  readonly companySiren = computed(() => this.company().siret.slice(0, 9));
  readonly externalLinks: WritableSignal<ExternalLink[]> = signal<ExternalLink[]>([]);
  readonly displayContracts: WritableSignal<boolean> = signal<boolean>(false);
  readonly displayUsers: WritableSignal<boolean> = signal<boolean>(false);
  readonly displayContributors: WritableSignal<boolean> = signal<boolean>(false);
  readonly displayLya: WritableSignal<boolean> = signal<boolean>(false);
  readonly insideCompany: WritableSignal<boolean> = signal<boolean>(false);

  readonly siretMask = SIRET_MASK;

  constructor(
    @Inject(TIIME_OVERLAY_DATA) private data: CompanyOverviewOverlayData,
    private readonly commonDataApiService: CommonDataApiService,
    private readonly navigationService: NavigationService,
    private readonly userApiService: UserApiService,
    private readonly companyApiService: CompanyApiService,
    private readonly externalUrlService: ExternalUrlService,
    private readonly exonerationZonesDialog: ExonerationZonesDialogService,
    private readonly ceateOrUpdateExternalLinkDialogService: CreateOrUpdateExternalLinkDialogService,
    private readonly externalLinkApiService: ExternalLinkApiService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly snackbar: TiimeSnackbarService,
    private readonly permissionApiService: PermissionApiService,
    private readonly store: Store<AppStoreState>,
    private readonly contractFacade: ContractFacade,
    private readonly sendToLyaDialogService: SendToLyaDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initCompany();

    this.initCompanyPermittedContainers();

    this.insideCompany.set(this.data.insideCompany);

    if (this.insideCompany()) {
      this.initExternalLinks();
    }
  }

  openPappers(): void {
    this.externalUrlService.openPappers(this.companySiren());
  }

  openInfogreffe(): void {
    this.externalUrlService.openInfogreffe(this.companySiren());
  }

  openLink(externalLink: ExternalLink): void {
    if (!this.insideCompany()) {
      return;
    }

    this.externalUrlService.openUrl(externalLink.url);
  }

  navigateToParameters(): void {
    if (!this.insideCompany()) {
      return;
    }

    this.navigationService.navigateFromCurrentCompany([CompanyRoute.parameters]);
    this.overlayRef.close();
  }

  openExonerationZonesDialog(): void {
    if (!this.insideCompany()) {
      return;
    }

    this.exonerationZonesDialog.open();
  }

  openCreateExternalLinkDialog(): void {
    if (!this.insideCompany()) {
      return;
    }

    const data: CreateOrUpdateExternalLinkDialogData = {
      externalLink: new ExternalLink()
    };

    this.ceateOrUpdateExternalLinkDialogService
      .open({ data })
      .afterClosed()
      .pipe(
        filter((createdExternalLink: ExternalLink) => !!createdExternalLink),
        tap((createdExternalLink: ExternalLink) => this.addOneExternalLink(createdExternalLink))
      )
      .subscribe();
  }

  openUpdateExternalLinkDialog(externalLink: ExternalLink): void {
    if (!this.insideCompany()) {
      return;
    }

    const data: CreateOrUpdateExternalLinkDialogData = {
      externalLink
    };

    this.ceateOrUpdateExternalLinkDialogService
      .open({ data })
      .afterClosed()
      .pipe(
        filter((updatedExternalLink: ExternalLink) => !!updatedExternalLink),
        tap((updatedExternalLink: ExternalLink) => this.updateOneExternalLink(updatedExternalLink))
      )
      .subscribe();
  }

  openDeleteExternalLinkDialog(externalLink: ExternalLink, index: number): void {
    if (!this.insideCompany()) {
      return;
    }

    this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Êtes-vous sûr de vouloir supprimer le lien ${externalLink.name} ?`,
          'Supprimer',
          'Annuler',
          null,
          'warn'
        )
      })
      .afterClosed()
      .pipe(
        filter((confirm: boolean) => !!confirm),
        switchMap(() => this.externalLinkApiService.deleteExternalLink(externalLink.id)),
        tap(() => {
          this.removeOneExternalLink(index);
          this.snackbar.openSuccess('Le lien a bien été supprimé.');
        })
      )
      .subscribe();
  }

  updateContract(contract: Contract): void {
    this.contractFacade
      .openUpdateContractDialog(contract.id)
      .pipe(
        tap((updatedContract: Contract) =>
          this.contracts.update((contracts: Contract[]) => ArrayHelper.updateOne(contracts, updatedContract))
        )
      )
      .subscribe();
  }

  changeContractStatus(event: ChangeContractStatusType): void {
    if (event.contract.status === event.status) {
      return;
    }

    this.contractFacade
      .openConfirmUpdateContractStatus(event.contract.status, event.status, false)
      .pipe(
        filter((data: ReturnedUpdateContractStatusDialogData) => !!data),
        switchMap((data: ReturnedUpdateContractStatusDialogData) =>
          this.contractFacade.updateContractStatus(event.contract.id, event.status, data.lossReason, data.lossComment)
        ),
        tap((updatedContract: Contract) => {
          this.contracts.update((contracts: Contract[]) => ArrayHelper.updateOne(contracts, updatedContract));
          this.snackbar.openSuccess(`L'État du contrat a bien été modifié`);
        })
      )
      .subscribe();
  }

  sendToLya(): void {
    this.companyApiService
      .getLyaStatus(this.data.companyId)
      .pipe(
        switchMap((lyaStatus: LyaStatus | null) =>
          this.sendToLyaDialogService
            .open({ data: { companyId: this.data.companyId, status: lyaStatus } as SendToLyaDialogData })
            .afterClosed()
        )
      )
      .subscribe();
  }

  private initCompany(): void {
    this.companyApiService
      .getCompanyOverview(this.data.companyId)
      .pipe(
        tap((company: Company) => {
          this.company.set(company);
          setTimeout(() => this.overlayRef.updatePosition());
        })
      )
      .subscribe();
  }

  private initCompanyPermittedContainers(): void {
    this.permissionApiService
      .getCompanyPermission(this.data.companyId)
      .pipe(
        withLatestFrom(this.store.pipe(select(businessUserSelector), take(1))),
        tap(([permission, businessUser]: [Permission, BusinessUser]) => {
          this.displayUsers.set(this.hasPermission(permission, PermissionAcl.navigationAccessContactParameters));
          this.displayContributors.set(this.hasPermission(permission, PermissionAcl.companyContributorsGet));
          this.displayContracts.set(
            (businessUser?.partner?.config?.contractModuleAllowed ?? false) &&
              this.hasPermission(permission, PermissionAcl.navigationAccessContractsParameters)
          );
          this.displayLya.set(
            (businessUser?.partner?.config?.lyaAllowed ?? false) &&
              this.hasPermission(permission, PermissionAcl.companyLyaGet)
          );

          if (this.displayUsers()) {
            this.initUsers();
          }

          if (this.displayContributors()) {
            this.initContributors();
          }

          if (this.displayContracts()) {
            this.initContracts();
          }
        })
      )
      .subscribe();
  }

  private initContributors(): void {
    forkJoin([
      this.companyApiService.getContributors(this.data.companyId),
      this.companyApiService.getContributorJobs(this.data.companyId)
    ])
      .pipe(
        tap(([companyContributors, contributorJobs]: [CompanyContributor[], ContributorJob[]]) => {
          // order contributor jobs
          const ordererContributorsJobsTypes: ContributorJobType[] = [
            ContributorJobType.missionChief,
            ContributorJobType.expertAccountant,
            ContributorJobType.supervisor,
            ContributorJobType.collaborator,
            ContributorJobType.assistant,
            ContributorJobType.legalReferent,
            ContributorJobType.socialReferent
          ];

          const sortedContributorJobs: ContributorJob[] = contributorJobs.sort(
            (a: ContributorJob, b: ContributorJob) => {
              const indexA: number = ordererContributorsJobsTypes.indexOf(a.type);
              const indexB: number = ordererContributorsJobsTypes.indexOf(b.type);

              // Si le type de 'a' n'est pas dans l'ordre, mettez-le à la fin (ou gérez autrement)
              const validIndexA: number = indexA !== -1 ? indexA : ordererContributorsJobsTypes.length;
              const validIndexB: number = indexB !== -1 ? indexB : ordererContributorsJobsTypes.length;

              return validIndexA - validIndexB;
            }
          );

          // order company contributors by contributor jobs
          const sortedCompanyContributor: CompanyContributor[] = companyContributors.sort(
            (a: CompanyContributor, b: CompanyContributor) => {
              const indexA: number = sortedContributorJobs.findIndex(
                (contributorJob: ContributorJob) => contributorJob.id === a.job?.id
              );
              const indexB: number = sortedContributorJobs.findIndex(
                (contributorJob: ContributorJob) => contributorJob.id === b.job?.id
              );

              // Si le type de 'a' n'est pas dans l'ordre, mettez-le à la fin (ou gérez autrement)
              const validIndexA: number = indexA !== -1 ? indexA : sortedContributorJobs.length;
              const validIndexB: number = indexB !== -1 ? indexB : sortedContributorJobs.length;

              return validIndexA - validIndexB;
            }
          );

          this.companyContributors.set(sortedCompanyContributor);
          this.contributorJobs.set(sortedContributorJobs);
        })
      )
      .subscribe();
  }

  private initUsers(): void {
    this.userApiService
      .getUsersOverview(this.data.companyId)
      .pipe(tap((users: User[]) => this.users.set(users)))
      .subscribe();
  }

  private initContracts(): void {
    this.contractFacade
      .getCompanyContracts(this.data.companyId)
      .pipe(tap((contrats: Contract[]) => this.contracts.set(contrats)))
      .subscribe();
  }

  private initExternalLinks(): void {
    this.externalLinkApiService
      .getExternalLinks()
      .pipe(tap((externalLinks: ExternalLink[]) => this.externalLinks.set(externalLinks)))
      .subscribe();
  }

  private addOneExternalLink(createdExternalLink: ExternalLink): void {
    this.externalLinks.update((externalLinks: ExternalLink[]) =>
      ArrayHelper.addOne(externalLinks, createdExternalLink)
    );
  }

  private updateOneExternalLink(updatedExternalLink: ExternalLink): void {
    this.externalLinks.update((externalLinks: ExternalLink[]) =>
      ArrayHelper.updateOne(externalLinks, updatedExternalLink)
    );
  }

  private removeOneExternalLink(index: number): void {
    this.externalLinks.update((externalLinks: ExternalLink[]) => ArrayHelper.removeOne(externalLinks, index));
  }

  private hasPermission(permission: Permission, acl: PermissionAcl): boolean {
    if (!permission) {
      return true;
    }

    return permission.permissionAttributes.some(attribute => attribute.acl === acl && attribute.permit);
  }
}
